import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input } from '@angular/core';
import { translate, TranslocoService } from '@jsverse/transloco';

import { Router } from '@angular/router';
import { IFacetNodeModel, selectTranslate } from '@libs/vc-core-lib';
import highcharts, { SeriesBarOptions, SeriesClickEventObject, XAxisOptions } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { isEmpty } from 'lodash-es';
import { ChartWrapperComponent } from '../../chart-wrapper/chart-wrapper.component';

@Component({
    selector: 'vc-findings-by-category',
    imports: [HighchartsChartComponent, ChartWrapperComponent],
    templateUrl: './findings-by-category-chart.component.html',
    styleUrl: './findings-by-category-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingsByCategoryChartComponent {
    private readonly _router: Router = inject(Router);
    private readonly _transloco = inject(TranslocoService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    highcharts = highcharts;

    constructor() {
        selectTranslate(
            this._transloco,
            () => {
                this._title = translate('LANDING_DASHBOARD.FINDINGS_BY_CATEGORY.TITLE');
                if (this.options.series?.[0]) {
                    this.options.series[0].name = this._title;
                }
                if (this._facets) {
                    this._buildFindingsByCategories(this._facets);
                }
                this.options = { ...this.options };
                this._changeDetectorRef.markForCheck();
            },
            this._destroyRef
        );
    }

    @Input()
    contentHeight!: string;

    @Input()
    height!: string;

    @Input()
    loading = false;

    @Input()
    updating = false;

    @Input()
    set title(value: string) {
        this._title = value;
        if (this.options.series) {
            this.options.series[0].name = value;
        }
    }
    get title(): string {
        return this._title;
    }
    private _title: string = translate('LANDING_DASHBOARD.FINDINGS_BY_CATEGORY.TITLE');

    @Input()
    set facets(value: IFacetNodeModel[]) {
        this._buildFindingsByCategories(value);
        this._facets = value;
    }
    get facets(): IFacetNodeModel[] {
        return this._facets;
    }
    private _facets!: IFacetNodeModel[];

    @Input()
    onSeriesClick?: (event: SeriesClickEventObject) => void;

    options: Highcharts.Options = {
        chart: {
            type: 'bar',
            spacing: [20, 24, 16, 24],
            borderWidth: 0,
        },
        title: {
            text: '',
        },
        xAxis: {
            type: 'category',
            categories: [],
            labels: {
                y: -13,
                x: 0,
                align: 'left',
                style: {
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
            },
            title: {
                text: undefined,
            },
            lineWidth: 0,
            scrollbar: {
                enabled: false,
            },
        },
        yAxis: {
            title: { text: '' },
            labels: {
                enabled: true,
                format: '{value}',
            },
            stackLabels: {
                enabled: false,
            },
        },
        plotOptions: {
            bar: {
                pointWidth: 11,
                colors: [
                    'var(--primary-600)',
                    'var(--secondary-600)',
                    'var(--primary-500)',
                    'var(--secondary-500)',
                    'var(--primary-400)',
                ],
                colorByPoint: true,
            },
            series: {
                stacking: 'normal',
                cursor: 'pointer',
                dataLabels: {
                    enabled: false,
                },
                events: {
                    click: (event: SeriesClickEventObject) => {
                        if (this.onSeriesClick) {
                            this.onSeriesClick(event);
                            return;
                        }
                        this._router.navigate([], {
                            queryParams: {
                                category: event.point.category ?? null,
                            },
                        });
                    },
                },
            },
        },
        legend: {
            enabled: false,
        },
        series: [
            {
                name: this.title,
                data: [],
                type: 'bar',
            },
        ],
        tooltip: {
            outside: true,
        },
    };

    private _buildFindingsByCategories(facets: IFacetNodeModel[]): void {
        (this.options.xAxis as XAxisOptions).categories = [];
        (this.options.series?.[0] as SeriesBarOptions).data = [];

        if (!isEmpty(facets)) {
            // The category limit to be shown on the chart is set to 5
            (this.options.xAxis as XAxisOptions).categories = facets
                .map((facet) => facet.value)
                .filter((facet, index) => index <= 4);
            (this.options.series?.[0] as SeriesBarOptions).data = facets
                .map((facet) => facet.count)
                .filter((facet, index) => index <= 4);
        }
    }
}
